import type { OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { merge } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { VideoEditMenuCallbacks, VideoEditMenuData } from '@core/prosemirror-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormErrorDirective,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
} from '@core/user-interface-legacy';

import { VideoParserService } from '../video-parser.service';

type VideoEditDialogData = {
  initData: VideoEditMenuData;
  callbacks: VideoEditMenuCallbacks;
};

@Component({
  selector: 'rt-video-edit-dialog',
  templateUrl: 'video-edit-dialog.component.html',
  styleUrls: ['video-edit-dialog.component.less'],
  providers: [VideoParserService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    DialogFooterComponent,
    IconComponent,
    ButtonComponent,
    FormErrorDirective,
    TranslatePipe,
  ],
})
export class VideoEditDialogComponent
  implements DialogComponentI<VideoEditDialogData, void>, OnInit
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly videoParserService = inject(VideoParserService);

  public src: string;
  public isEditing = false;
  public errors = {
    invalidSource: this.i18n.translate('wysiwyg.video_plugin.invalid_source'),
  };
  public formControl: FormControl;

  private callbacks: VideoEditMenuCallbacks;

  public ngOnInit(): void {
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public setDialogData({ initData, callbacks }: VideoEditDialogData): void {
    this.src = initData.src;
    this.formControl = new FormControl(this.src, [this.getSourceValidator()]);
    this.isEditing = !!this.src;
    this.callbacks = callbacks;
    this.cdRef.detectChanges();
  }

  public changeSource(): void {
    const src = this.videoParserService.parse(this.formControl.value);

    this.callbacks.changeSource({
      src,
      isEditing: this.isEditing,
    });
  }

  public getSourceValidator() {
    return (control: AbstractControl): ValidationErrors | null =>
      this.videoParserService.parse(control.value) === ''
        ? { invalidSource: { value: control.value } }
        : null;
  }

  public delete(): void {
    this.callbacks.delete?.();
  }

  public cancel(): void {
    this.callbacks.cancel();
  }
}
